// Helper to safely convert string to number for display
const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  if (typeof value === 'string') {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  }
  return typeof value === 'number' ? value : 0;
};

const formatCurrency = (amount) => {
  // Handle both string and number inputs
  const numAmount = toNumber(amount);
  return new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency: 'PKR'
  }).format(numAmount);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-PK');
};

const getCarDisplayName = (car) => {
  if (!car) return 'N/A';
  const reg = car.registrationNumber || '';
  const chassis = car.chassisNumber || '';
  const engine = car.engineNumber || '';
  return `${car.name} ${car.vehicleModel} ${reg ? `(${reg})` : chassis ? `(${chassis})` : engine ? `(${engine})` : ''}`.trim();
};

const getCustomerDisplayName = (customer) => {
  if (!customer) return 'N/A';
  const identifier = customer.cnic || customer.phoneNumber || 'N/A';
  return `${customer.fullName} (${identifier})`;
};

const buildShowroomHeader = (details = {}) => {
  const name = details.showroomName || details.name || 'Showroom';
  const contact = details.showroomPhone || details.contact || 'N/A';
  const email = details.showroomEmail || details.email || 'N/A';
  const address = details.showroomAddress || details.address || 'N/A';
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map(part => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || 'SM';

  return `
    <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
      <div style="width: 64px; height: 64px; border-radius: 10px; background-color: #eef2ff; color: #4338ca; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 18px; border: 1px solid #e5e7eb;">
        ${initials}
      </div>
      <div style="text-align: left;">
        <div style="font-size: 18px; font-weight: 700; margin-bottom: 4px;">${name}</div>
        <div style="font-size: 11px; color: #6b7280; margin-bottom: 2px;">Address: ${address}</div>
        <div style="font-size: 11px; color: #6b7280; margin-bottom: 2px;">Contact: ${contact}</div>
        <div style="font-size: 11px; color: #6b7280;">Email: ${email}</div>
      </div>
    </div>
  `;
};

const generatePDFContent = (deal, exportType, showroomDetails = {}) => {
  const isPurchase = exportType === 'purchase';
  const payments = isPurchase ? (deal.purchasePaymentStructures || []) : (deal.sellPaymentStructures || []);
  const customer = isPurchase ? deal.purchaseInfo?.customer : deal.sellInfo?.customer;
  const dealInfo = isPurchase ? deal.purchaseInfo : deal.sellInfo;
  const title = isPurchase ? 'Purchase Payment Transactions' : 'Sale Payment Transactions';
  const customerType = isPurchase ? 'Seller' : 'Purchaser';

  return `
    <div style="margin-bottom: 16px; border-bottom: 2px solid #111827; padding-bottom: 14px;">
      ${buildShowroomHeader(showroomDetails)}
      <div style="font-size: 16px; font-weight: 700; margin-top: 6px;">${title}</div>
      <div style="font-size: 11px; color: #6b7280;">Generated on: ${new Date().toLocaleString('en-PK')}</div>
      <div style="font-size: 11px; color: #6b7280;">Deal ID: ${deal.dealId}</div>
    </div>
    
    <div style="margin-bottom: 14px; padding: 12px; background-color: #f9fafb; border-radius: 8px; border: 1px solid #e5e7eb;">
      <h3 style="margin: 0 0 8px 0; font-size: 14px;">Deal Information</h3>
      <p style="margin: 4px 0; font-size: 12px;"><strong>Deal ID:</strong> ${deal.dealId}</p>
      <p style="margin: 4px 0; font-size: 12px;"><strong>Vehicle:</strong> ${getCarDisplayName(deal.car)}</p>
      <p style="margin: 4px 0; font-size: 12px;"><strong>Registration:</strong> ${deal.car?.registrationNumber || 'N/A'}</p>
      <p style="margin: 4px 0; font-size: 12px;"><strong>Chassis:</strong> ${deal.car?.chassisNumber || 'N/A'}</p>
      <p style="margin: 4px 0; font-size: 12px;"><strong>Color:</strong> ${deal.car?.color || 'N/A'}</p>
    </div>

    <div style="margin-bottom: 14px; padding: 12px; background-color: #f9fafb; border-radius: 8px; border: 1px solid #e5e7eb;">
      <h3 style="margin: 0 0 8px 0; font-size: 14px;">${customerType} Information</h3>
      <p style="margin: 4px 0; font-size: 12px;"><strong>Name:</strong> ${getCustomerDisplayName(customer)}</p>
      <p style="margin: 4px 0; font-size: 12px;"><strong>CNIC:</strong> ${customer?.cnic || 'N/A'}</p>
      <p style="margin: 4px 0; font-size: 12px;"><strong>Phone:</strong> ${customer?.phoneNumber || 'N/A'}</p>
      <p style="margin: 4px 0; font-size: 12px;"><strong>Client Type:</strong> ${customer?.clientType || 'N/A'}</p>
      <p style="margin: 4px 0; font-size: 12px;"><strong>${isPurchase ? 'Purchase' : 'Sale'} Value:</strong> ${formatCurrency(dealInfo?.value)}</p>
      <p style="margin: 4px 0; font-size: 12px;"><strong>${isPurchase ? 'Purchase' : 'Sale'} Date:</strong> ${formatDate(dealInfo?.dealDate)}</p>
    </div>
    
    <table style="width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 11px;">
      <thead>
        <tr style="background-color: #f3f4f6;">
          <th style="border: 1px solid #e5e7eb; padding: 8px; text-align: left; font-weight: 700;">Sr. No.</th>
          <th style="border: 1px solid #e5e7eb; padding: 8px; text-align: left; font-weight: 700;">Amount</th>
          <th style="border: 1px solid #e5e7eb; padding: 8px; text-align: left; font-weight: 700;">Description</th>
          <th style="border: 1px solid #e5e7eb; padding: 8px; text-align: left; font-weight: 700;">Due Date</th>
          <th style="border: 1px solid #e5e7eb; padding: 8px; text-align: left; font-weight: 700;">Status</th>
          <th style="border: 1px solid #e5e7eb; padding: 8px; text-align: left; font-weight: 700;">Received Date</th>
        </tr>
      </thead>
      <tbody>
        ${payments.map((payment, index) => `
          <tr>
            <td style="border: 1px solid #e5e7eb; padding: 8px; text-align: left;">${index + 1}</td>
            <td style="border: 1px solid #e5e7eb; padding: 8px; text-align: left;">${formatCurrency(payment.amount)}</td>
            <td style="border: 1px solid #e5e7eb; padding: 8px; text-align: left;">${payment.description || 'N/A'}</td>
            <td style="border: 1px solid #e5e7eb; padding: 8px; text-align: left;">${formatDate(payment.dueDate)}</td>
            <td style="border: 1px solid #e5e7eb; padding: 8px; text-align: left;">
              <span style="padding: 2px 6px; border-radius: 4px; font-size: 10px; background-color: ${payment.status === 'RECEIVED' ? '#dcfce7' : payment.status === 'DUE' ? '#fef3c7' : '#fee2e2'}; color: ${payment.status === 'RECEIVED' ? '#166534' : payment.status === 'DUE' ? '#92400e' : '#dc2626'};">
                ${payment.status}
              </span>
            </td>
            <td style="border: 1px solid #e5e7eb; padding: 8px; text-align: left;">${payment.status === 'RECEIVED' ? formatDate(payment.receivedAt) : 'N/A'}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;
};

const exportPaymentTransactionsPDF = async (deal, exportType = 'sale', showroomDetails = {}, onProgress) => {
  if (!deal) return;

  let pdfContent = null;
  try {
    // Show loading state
    if (onProgress) onProgress(true);

    const { default: jsPDF } = await import('jspdf');
    const html2canvas = await import('html2canvas');

    pdfContent = document.createElement('div');
    pdfContent.style.position = 'absolute';
    pdfContent.style.left = '-9999px';
    pdfContent.style.top = '0';
    pdfContent.style.width = '800px'; // Reduced from 1200px for faster rendering
    pdfContent.style.padding = '20px';
    pdfContent.style.fontFamily = 'Arial, sans-serif';
    pdfContent.style.backgroundColor = 'white';
    pdfContent.style.color = 'black';

    pdfContent.innerHTML = generatePDFContent(deal, exportType, showroomDetails);
    document.body.appendChild(pdfContent);

    // Convert to canvas - optimized for speed (scale 1 = fastest, still good quality)
    const canvas = await html2canvas.default(pdfContent, {
      scale: 1, // Reduced from 2 for much faster rendering
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      logging: false,
      removeContainer: true
    });

    document.body.removeChild(pdfContent);
    pdfContent = null;

    // Use JPEG with quality for faster processing (smaller file, faster conversion)
    const imgData = canvas.toDataURL('image/jpeg', 0.92);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Add footer to all pages
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(100, 100, 100);
      pdf.text('powered by ledger Glide | +923332416545', imgWidth - 10, pageHeight - 5, { align: 'right' });
    }

    const title = exportType === 'purchase' ? 'Purchase' : 'Sale';
    const showroomName = (showroomDetails.showroomName || showroomDetails.name || '').replace(/\s+/g, '_') || 'Showroom';
    const fileName = `${showroomName}_${title}_Payment_Transactions_${deal.dealId}_${new Date().toISOString().split('T')[0]}.pdf`;
    pdf.save(fileName);
    
    if (onProgress) onProgress(false);
  } catch (error) {
    console.error('Error generating payment transactions PDF:', error);
    if (pdfContent && pdfContent.parentNode) {
      document.body.removeChild(pdfContent);
    }
    if (onProgress) onProgress(false);
    alert('Error generating PDF. Please try again.');
  }
};

export default exportPaymentTransactionsPDF;